import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const CountryWaitlistPage = () => {
  const [searchParams] = useSearchParams();
  const urlCountry = searchParams.get("country");

  const [formData, setFormData] = useState({
    email: "",
    name: "",
    country: urlCountry || "US", // Default to US until detection completes
    interestedIn: "customer",
    businessType: "",
    phone: "",
    city: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detectedCountryName, setDetectedCountryName] = useState("");

  // Auto-detect country from IP on component mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/waitlist/country/detect`,
        );
        const data = await response.json();

        if (data.success && data.country) {
          // Only update if no URL param was provided
          if (!urlCountry) {
            setFormData((prev) => ({
              ...prev,
              country: data.country,
            }));
          }
          setDetectedCountryName(data.countryName || data.country);
        }
      } catch (err) {
        setDetectedCountryName("United States");
      }
    };

    detectCountry();
  }, [urlCountry]);

  const countryOptions = [
    { code: "US", name: "United States" },
    { code: "CA", name: "Canada" },
    { code: "GB", name: "United Kingdom" },
    { code: "NG", name: "Nigeria" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "IN", name: "India" },
    { code: "AU", name: "Australia" },
    { code: "BR", name: "Brazil" },
    { code: "MX", name: "Mexico" },
    { code: "JP", name: "Japan" },
    { code: "CN", name: "China" },
    { code: "ZA", name: "South Africa" },
    { code: "KE", name: "Kenya" },
    { code: "GH", name: "Ghana" },
  ];

  const selectedCountryName =
    countryOptions.find((c) => c.code === formData.country)?.name ||
    detectedCountryName;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/waitlist/country/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          source: "geo_blocked",
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await response.json()
        : {};

      if (!response.ok) {
        throw new Error(data.error || data.message || "Failed to join waitlist");
      }

      if (!data.success && !data.alreadyExists) {
        throw new Error(data.error || data.message || "Failed to join waitlist");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✅</div>
          <h1 style={styles.heading}>You're on the list!</h1>
          <p style={styles.text}>
            Thank you for your interest in OGESTYLE. We'll notify you as soon as
            we launch in {selectedCountryName}.
          </p>
          <p style={styles.subtext}>
            In the meantime, follow us on social media for updates and exclusive
            pre-launch offers.
          </p>
          <div style={styles.socialLinks}>
            <a href="https://instagram.com/ogestyle" style={styles.socialLink}>
              Instagram
            </a>
            <a href="https://facebook.com/ogestyle" style={styles.socialLink}>
              Facebook
            </a>
            <a href="https://twitter.com/ogestyle" style={styles.socialLink}>
              Twitter
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoText}>OGESTYLE</span>
        </div>

        <h1 style={styles.heading}>
          We're not available in {selectedCountryName} yet
        </h1>

        <p style={styles.text}>
          OGESTYLE is currently available in the United States only. We're
          working hard to expand to more countries soon!
        </p>

        <div style={styles.benefitsSection}>
          <h3 style={styles.subheading}>Join our waitlist to:</h3>
          <ul style={styles.benefitsList}>
            <li>🔔 Get notified when we launch in your country</li>
            <li>🎁 Receive exclusive early-bird offers</li>
            <li>💬 Share what services you'd like to see</li>
            <li>🌍 Help us prioritize which countries to launch next</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <input
              type="text"
              name="name"
              placeholder="Your Name *"
              value={formData.name}
              onChange={handleChange}
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formRow}>
            <input
              type="email"
              name="email"
              placeholder="Email Address *"
              value={formData.email}
              onChange={handleChange}
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formRow}>
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number (optional)"
              value={formData.phone}
              onChange={handleChange}
              style={styles.input}
            />
          </div>

          <div style={styles.formRow}>
            <input
              type="text"
              name="city"
              placeholder="City"
              value={formData.city}
              onChange={handleChange}
              style={styles.input}
            />
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>Your Country *</label>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              style={styles.select}
              required
            >
              {countryOptions.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>I'm interested in:</label>
            <select
              name="interestedIn"
              value={formData.interestedIn}
              onChange={handleChange}
              style={styles.select}
              required
            >
              <option value="customer">Booking services (Customer)</option>
              <option value="business_owner">
                Opening a salon/spa (Business Owner)
              </option>
              <option value="freelancer">
                Offering my services (Freelancer)
              </option>
              <option value="other">Just exploring</option>
            </select>
          </div>

          {formData.interestedIn === "business_owner" && (
            <div style={styles.formRow}>
              <input
                type="text"
                name="businessType"
                placeholder="What type of business? (e.g., Hair Salon, Nail Spa)"
                value={formData.businessType}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
          )}

          {error && <div style={styles.error}>{error}</div>}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.button,
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
          >
            {isSubmitting ? "Joining..." : "Join Waitlist"}
          </button>
        </form>

        <p style={styles.footerText}>
          Using a VPN? Try disabling it to access OGESTYLE if you're actually in
          one of our supported countries.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
    padding: "20px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    maxWidth: "600px",
    width: "100%",
    background: "white",
    borderRadius: "16px",
    padding: "48px 40px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  },
  logo: {
    textAlign: "center",
    marginBottom: "24px",
  },
  logoText: {
    fontSize: "32px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  successIcon: {
    fontSize: "64px",
    textAlign: "center",
    marginBottom: "16px",
  },
  heading: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1f2937",
    marginBottom: "16px",
    textAlign: "center",
    lineHeight: "1.3",
  },
  text: {
    fontSize: "16px",
    color: "#6b7280",
    lineHeight: "1.6",
    marginBottom: "24px",
    textAlign: "center",
  },
  subtext: {
    fontSize: "14px",
    color: "#9ca3af",
    lineHeight: "1.6",
    marginBottom: "24px",
    textAlign: "center",
  },
  benefitsSection: {
    background: "#f9fafb",
    padding: "24px",
    borderRadius: "12px",
    marginBottom: "32px",
  },
  subheading: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "16px",
  },
  benefitsList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formRow: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
  },
  input: {
    padding: "12px 16px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "15px",
    fontFamily: "inherit",
    outline: "none",
    transition: "border-color 0.2s",
  },
  select: {
    padding: "12px 16px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "15px",
    fontFamily: "inherit",
    outline: "none",
    backgroundColor: "white",
    cursor: "pointer",
  },
  button: {
    padding: "14px 24px",
    background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    marginTop: "8px",
  },
  error: {
    padding: "12px",
    background: "#fee2e2",
    color: "#dc2626",
    borderRadius: "8px",
    fontSize: "14px",
    textAlign: "center",
  },
  socialLinks: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginTop: "24px",
  },
  socialLink: {
    padding: "10px 20px",
    background: "#f3f4f6",
    color: "#6b7280",
    borderRadius: "8px",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background 0.2s",
  },
  footerText: {
    fontSize: "13px",
    color: "#9ca3af",
    textAlign: "center",
    marginTop: "24px",
    lineHeight: "1.5",
  },
};

export default CountryWaitlistPage;
